// PaginationControl — shared dashboard list-page pagination footer.
//
// Spec: docs/specs/dashboard-pagination.md §5 + §7.2.
//
// Pair with usePagination hook: the hook owns state, this component is
// pure render + callbacks. Renders nothing when total = 0 (empty list
// shows EmptyState, not pagination).

import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PAGE_SIZE_OPTIONS } from "../lib/usePagination.js";

export interface PaginationControlProps {
  /** 1-indexed page. */
  page: number;
  pageSize: number;
  /** Total rows for the current query (server-reported). null = unknown
   *  (e.g. server hasn't returned `total` — we still render basic
   *  prev/next based on whether `hasMore` is true). */
  total: number | null;
  /** Optional. When `total` is null but we know whether more pages
   *  exist (server returned a `has_more`-style flag), surface it so
   *  the "next" button stays clickable. Default: assume true while
   *  total is null. */
  hasMore?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Defaults to PAGE_SIZE_OPTIONS [10, 25, 50, 100]. */
  pageSizeOptions?: readonly number[];
  /** Disable all controls (e.g. during fetch). */
  loading?: boolean;
}

/** Format an integer with thousand separators per zh-TW conventions. */
function fmt(n: number): string {
  return new Intl.NumberFormat("zh-TW").format(n);
}

export default function PaginationControl({
  page,
  pageSize,
  total,
  hasMore,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  loading = false,
}: PaginationControlProps): React.ReactElement | null {
  const totalPages = total != null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const knownLastPage = totalPages != null;
  const isFirstPage = page <= 1;
  const isLastPage = totalPages != null ? page >= totalPages : hasMore === false;

  // Local input state for the「跳到第 N 頁」inline number. Synced to
  // `page` when the prop changes (so external resets reflect here).
  const [pageInput, setPageInput] = useState<string>(String(page));
  useEffect(() => { setPageInput(String(page)); }, [page]);

  // Keyboard shortcuts: `[` prev / `]` next. Ignored when focus is in
  // any input / textarea / contenteditable. Listener at document level
  // so it works across the page (not just when the footer is focused).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (loading) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (
        t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.tagName === "SELECT" ||
        t.isContentEditable
      )) return;
      if (e.key === "[" && !isFirstPage) {
        e.preventDefault();
        onPageChange(page - 1);
      } else if (e.key === "]" && !isLastPage) {
        e.preventDefault();
        onPageChange(page + 1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [page, isFirstPage, isLastPage, loading, onPageChange]);

  const submitPageInput = useCallback((): void => {
    const n = parseInt(pageInput, 10);
    if (!Number.isFinite(n) || n < 1) {
      setPageInput(String(page));
      return;
    }
    const clamped = totalPages != null ? Math.min(totalPages, n) : n;
    if (clamped !== page) onPageChange(clamped);
    else setPageInput(String(page));
  }, [pageInput, page, totalPages, onPageChange]);

  // Hide entirely when we know there are 0 results. (The caller shows
  // an EmptyState in that case.)
  if (total === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-border px-4 py-3 text-xs text-text-secondary">
      <div className="tabular-nums">
        {knownLastPage ? (
          <>
            第 <span className="text-text-primary font-medium">{page}</span> / {totalPages} 頁
            ・ 共 <span className="text-text-primary font-medium">{fmt(total!)}</span> 筆
          </>
        ) : (
          <>第 <span className="text-text-primary font-medium">{page}</span> 頁</>
        )}
      </div>

      <div className="flex items-center gap-2">
        <label className="text-text-muted">每頁</label>
        <select
          className="bg-bg-base border border-border rounded text-xs px-2 py-1 text-text-primary disabled:opacity-50"
          value={pageSize}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          disabled={loading}
          aria-label="每頁筆數"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>

        <button
          type="button"
          className="btn-ghost h-7 w-7 p-0 disabled:opacity-30"
          onClick={() => onPageChange(page - 1)}
          disabled={loading || isFirstPage}
          aria-label="上一頁"
          title="上一頁 ([)"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Inline 跳頁 input — appears only when there are at least 6 pages,
            avoiding clutter on small result sets. */}
        {knownLastPage && totalPages! >= 6 ? (
          <input
            type="number"
            min={1}
            max={totalPages!}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={submitPageInput}
            onKeyDown={(e) => { if (e.key === "Enter") submitPageInput(); }}
            disabled={loading}
            className="w-12 bg-bg-base border border-border rounded text-xs px-1.5 py-1 text-text-primary text-center disabled:opacity-50"
            aria-label="跳至第 N 頁"
          />
        ) : (
          <span className="text-text-primary font-medium tabular-nums w-6 text-center">{page}</span>
        )}

        <button
          type="button"
          className="btn-ghost h-7 w-7 p-0 disabled:opacity-30"
          onClick={() => onPageChange(page + 1)}
          disabled={loading || isLastPage}
          aria-label="下一頁"
          title="下一頁 (])"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
